import { useParams, Link } from "react-router-dom";
import { videos } from "@/mocks/videos";
import { categories } from "@/mocks/categories";
import SEOHead from "@/components/feature/SEOHead";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((v) => v.id === Number(id));

  if (!video) {
    return (
      <div className="w-full px-4 md:px-6 py-20 max-w-screen-2xl mx-auto text-center">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
          <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-bold text-foreground-950 mb-2">视频未找到</h1>
        <p className="text-sm text-foreground-600 mb-6">抱歉，您要查看的视频不存在或已被移除</p>
        <Link
          to="/"
          className="px-6 py-3 bg-primary-500 text-white rounded-md font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === video.category);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: video.title,
    description: video.description,
    datePublished: video.date,
    author: {
      "@type": "Organization",
      name: "樱花视频",
    },
    publisher: {
      "@type": "Organization",
      name: "樱花视频",
      url: "https://www.sakuramovie.com",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.sakuramovie.com/article/${video.id}`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "https://www.sakuramovie.com",
      },
      ...(categoryInfo
        ? [
            {
              "@type": "ListItem",
              position: 2,
              name: categoryInfo.name,
              item: `https://www.sakuramovie.com/category/${categoryInfo.slug}`,
            },
          ]
        : []),
      {
        "@type": "ListItem",
        position: categoryInfo ? 3 : 2,
        name: video.title,
        item: `https://www.sakuramovie.com/article/${video.id}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${video.title} - 樱花视频`}
        description={video.description}
        keywords={`樱花视频,${video.tags.join(",")},${video.title},视频在线观看`}
        canonicalUrl={`/article/${video.id}`}
        ogType="article"
        ogImage={video.cover}
        schemaData={{ ...articleSchema, ...breadcrumbSchema }}
      />

      <article className="w-full px-4 md:px-6 py-8 md:py-12 max-w-screen-2xl mx-auto">
        <div className="max-w-4xl mx-auto">
          <div className="relative w-full aspect-video rounded-lg overflow-hidden mb-6">
            <img
              src={video.cover}
              alt={video.title}
              title={`${video.title} - 樱花视频高清在线观看`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-white/90 cursor-pointer hover:bg-white transition-colors">
                <i className="ri-play-fill text-2xl md:text-3xl text-primary-500 ml-1"></i>
              </div>
            </div>
            <div className="absolute bottom-3 right-3 bg-black/70 text-white text-sm px-3 py-1 rounded">
              {video.duration}
            </div>
          </div>

          <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
            {video.title}
          </h1>

          <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-foreground-600">
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-eye-line"></i>
              </div>
              {video.views}次观看
            </span>
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line"></i>
              </div>
              {video.date}
            </span>
            {categoryInfo && (
              <Link
                to={`/category/${categoryInfo.slug}`}
                className="flex items-center gap-1 text-primary-500 hover:text-primary-600 cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-folder-line"></i>
                </div>
                {categoryInfo.name}
              </Link>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {video.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs bg-secondary-100 text-secondary-900 px-3 py-1 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="prose prose-sm max-w-none">
            <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
              {video.content}
            </p>
          </div>

          <div className="mt-10 pt-6 border-t border-background-200">
            <Link
              to={categoryInfo ? `/category/${categoryInfo.slug}` : "/"}
              className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 cursor-pointer font-medium"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              {categoryInfo ? `返回${categoryInfo.name}` : "返回首页"}
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}